import { Form, TextFieldControl, VALIDATION, useToast } from '@foxtaibox/foxtailbox-frontend-core';
import {
  GetUserQuery,
  useInitConfirmEmailMutation,
  useLinkEmailMutation,
  useSetCandidatePhoneNumberMutation,
  useUpdateFirstCompanyCandidateMutation,
} from '@generated/graphql';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import PhoneNumberFieldControl from 'components/form/FieldControls/PhoneNumberFieldControl';
import useResendTimer from 'hooks/useResendTimer';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getIsEmailNeedToBeConfirmed, getIsNotCompanyCandidate } from 'utils/user';
import { UserDataForm } from '../types';

interface UserDataProps {
  user: GetUserQuery['me'] | undefined;
}

const UserData = ({ user }: UserDataProps) => {
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = useToast();
  const { resendTimer, isResendTimerActive, startResendTimer } = useResendTimer(60);

  const form = useForm<UserDataForm>({
    values: {
      username: user?.candidateUsername?.telegramUsername || '',
      yandexEmail: user?.candidateUsername?.yandexEmail || '',
      fullName: user?.firstCompanyCandidate?.fullName || user?.fullName || '',
      phoneNumber: user?.phoneNumber || '',
      organization: user?.firstCompanyCandidate?.organization || '',
      position: user?.firstCompanyCandidate?.position || '',
      email: user?.firstCompanyCandidate?.email || user?.candidateUsername.email || '',
    },
  });

  const { handleSubmit } = form;

  const [updateFirstCompanyCandidate, { loading: candidateLoading }] =
    useUpdateFirstCompanyCandidateMutation();
  const [savePhoneNumber, { loading: phoneLoading }] = useSetCandidatePhoneNumberMutation();
  const [linkEmail, { loading: linkEmailLoading }] = useLinkEmailMutation();
  const [initConfirmEmail, { loading: initConfirmEmailLoading }] = useInitConfirmEmailMutation();
  const isFormDisabled = !user?.firstCompanyCandidate;
  const isNotCompanyCandidate = getIsNotCompanyCandidate(user);
  const isEmailNeedToBeConfirmed = getIsEmailNeedToBeConfirmed(user);

  const handleLinkEmail = async (email: string) => {
    linkEmail({ variables: { email } })
      .then((res) => {
        if (!res.data?.linkEmail) throw new Error('initConfirmEmail failed');
        showSuccessToast(t('register.submittedMessage', { email }));
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  const handleInitConfirmEmail = async () => {
    const isValid = await form.trigger('email');

    if (!isValid) return;

    const email = form.getValues('email');

    initConfirmEmail()
      .then((res) => {
        if (!res.data?.initConfirmEmail) throw new Error('initConfirmEmail failed');
        startResendTimer();
        showSuccessToast(t('register.submittedMessage', { email }));
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  const onSubmit = async (formData: UserDataForm) => {
    const { dirtyFields } = form.formState;

    try {
      if (dirtyFields.phoneNumber) {
        const resPhoneNumber = await savePhoneNumber({
          variables: { phoneNumber: formData.phoneNumber },
        });
        const isPhoneNumberSaved = !!resPhoneNumber.data?.setCandidatePhoneNumber.phoneNumber;
        if (!isPhoneNumberSaved) throw new Error(t('languageSelect.errorSavingNumber'));
      }

      if (dirtyFields.email && isNotCompanyCandidate) {
        await handleLinkEmail(formData.email);
      }

      if (dirtyFields.email || dirtyFields.fullName) {
        const resCandidate = await updateFirstCompanyCandidate({
          variables: {
            firstCompanyCandidate: {
              fullName: formData.fullName,
              email: dirtyFields.email ? formData.email : undefined,
            },
          },
        });
        if (!resCandidate.data?.updateFirstCompanyCandidate.email)
          throw new Error(t('errors.emailBusy'));
        if (!resCandidate.data?.updateFirstCompanyCandidate.fullName)
          throw new Error(t('errors.common'));
      }
      showSuccessToast(t('successMessage'));
    } catch (error: Error | unknown) {
      const errorMessage = error instanceof Error ? error.message : t('errors.common');
      showErrorToast(errorMessage);
    }
  };

  const emailConfirmButtonText = useMemo(() => {
    if (resendTimer === null) return 'Подтверить email';
    if (isResendTimerActive) return t('register.resendEmailWithTimer', { resendTimer });
    return t('register.resendEmail');
  }, [resendTimer, resendTimer]);

  return (
    <div>
      <Typography variant='h28' mb={{ xs: 1.5, sm: 3 }}>
        {t('user.dataTitle')}
      </Typography>
      <Form form={form}>
        <Stack spacing={3} mb={3}>
          <TextFieldControl
            name='username'
            label={t('user.usernameLabel')}
            placeholder={t('user.usernameLabel')}
            type='text'
            disabled
          />
          <TextFieldControl
            name='fullName'
            label={t('user.fullNameLabel')}
            placeholder={t('user.fullNameLabel')}
            type='text'
            rules={{
              required: true,
              ...VALIDATION({
                message: t('validation.userName'),
                maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
              }).userName,
            }}
            disabled={isFormDisabled}
          />
          <PhoneNumberFieldControl disabled={isFormDisabled} />
          <TextFieldControl
            name='organization'
            label={t('user.organizationLabel')}
            placeholder={t('user.organizationPlaceholder')}
            type='text'
            disabled
          />
          <TextFieldControl
            name='position'
            label={t('user.positionLabel')}
            placeholder={t('user.positionPlaceholder')}
            type='text'
            disabled
          />
          <Stack spacing={1}>
            <TextFieldControl
              name='email'
              label='Email'
              placeholder='Email'
              type='email'
              rules={{
                required: true,
                ...VALIDATION({
                  message: t('validation.invalidEmailCharacters'),
                  maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
                }).emailFull,
              }}
              disabled={isFormDisabled}
            />
            {isEmailNeedToBeConfirmed && (
              <Button
                variant='text'
                onClick={() => handleInitConfirmEmail()}
                disabled={
                  isResendTimerActive ||
                  initConfirmEmailLoading ||
                  form.getFieldState('email').isDirty
                }
                sx={{ justifyContent: 'flex-start' }}>
                {emailConfirmButtonText}
              </Button>
            )}
          </Stack>
          {user?.candidateUsername?.yandexEmail && (
            <TextFieldControl
              name='yandexEmail'
              label='Аккаунт Яндекс'
              placeholder='Аккаунт Яндекс'
              type='text'
              disabled
            />
          )}
        </Stack>
      </Form>
      <Tooltip title={isFormDisabled ? t('user.notAllowed') : undefined}>
        <span>
          <Button
            variant='contained'
            size='large'
            fullWidth
            onClick={handleSubmit(onSubmit)}
            loading={candidateLoading || phoneLoading || linkEmailLoading}
            disabled={isFormDisabled}>
            {t('save')}
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};

export default UserData;
